import { Box, Tooltip, Typography } from "@mui/material";
import { ReactNode, useState } from "react";
import {
	getLocationMapById,
	getOpenStreetMapsDetailById,
	LocationMap,
	OpenStreetMapsDetail,
} from "../../api/location";
import { useGlobalState } from "../globalState";

interface LocationDataTooltipProps {
	location_map_id: number | null;
	ostmd_id: number | null;
	locationText: string;
	children?: ReactNode;
	entityId?: number;
	entityType?: "Person" | "Role";
}

/**
 * Reusable tooltip component that displays LocationMap and OpenStreetMapsDetail
 * information for any entity with location data (Person, Role, etc.)
 *
 * Only visible for superusers. For non-superusers, renders plain text.
 */
export const LocationDataTooltip = ({
	location_map_id,
	ostmd_id,
	locationText,
	children,
	entityId,
	entityType,
}: LocationDataTooltipProps) => {
	const { currentUser } = useGlobalState();
	const [locationMap, setLocationMap] = useState<LocationMap | null>(null);
	const [ostmd, setOstmd] = useState<OpenStreetMapsDetail | null>(null);
	const [loading, setLoading] = useState(false);

	const fetchLocationData = async () => {
		if (!currentUser.is_superuser || loading) return;
		if (!location_map_id && !ostmd_id) return;

		setLoading(true);
		try {
			if (location_map_id) {
				const locMap = await getLocationMapById(location_map_id);
				setLocationMap(locMap);
			}
			if (ostmd_id) {
				const ostmdData = await getOpenStreetMapsDetailById(ostmd_id);
				setOstmd(ostmdData);
			}
		} catch (error) {
			console.error("Failed to fetch location data:", error);
		} finally {
			setLoading(false);
		}
	};

	// For non-superusers, just render the children or plain text
	if (!currentUser.is_superuser) {
		return children ? (
			<>{children}</>
		) : (
			<Typography data-component="location-text">{locationText}</Typography>
		);
	}

	const tooltipContent = (
		<Box sx={{ p: 1 }} data-component="location-tooltip-content">
			{loading && (
				<Typography data-component="location-loading-text">
					Loading...
				</Typography>
			)}
			{entityId && entityType && (
				<Box data-component="entity-info">
					<Typography
						variant="caption"
						sx={{ fontWeight: "bold" }}
						data-component="entity-title"
					>
						{entityType} ID
					</Typography>
					<Typography
						variant="caption"
						display="block"
						data-component="entity-id"
					>
						{entityId}
					</Typography>
				</Box>
			)}
			<Box
				sx={entityId && entityType ? { mt: 1 } : {}}
				data-component="location-text-info"
			>
				<Typography
					variant="caption"
					sx={{ fontWeight: "bold" }}
					data-component="location-text-title"
				>
					Location Text
				</Typography>
				<Typography
					variant="caption"
					display="block"
					data-component="location-text-value"
				>
					{locationText || "None"}
				</Typography>
			</Box>
			<Box sx={{ mt: 1 }} data-component="location-map-info">
				<Typography
					variant="caption"
					sx={{ fontWeight: "bold" }}
					data-component="location-map-title"
				>
					LocationMap
				</Typography>
				{locationMap ? (
					<>
						<Typography
							variant="caption"
							display="block"
							data-component="location-map-id"
						>
							ID: {locationMap.id}
						</Typography>
						<Typography
							variant="caption"
							display="block"
							data-component="location-urn-id"
						>
							URN ID: {locationMap.urnid}
						</Typography>
						<Typography
							variant="caption"
							display="block"
							data-component="location-name"
						>
							Name: {locationMap.location_name}
						</Typography>
						<Typography
							variant="caption"
							display="block"
							data-component="location-country-iso"
						>
							Country ISO: {locationMap.country_iso_code || "N/A"}
						</Typography>
					</>
				) : (
					<Typography
						variant="caption"
						display="block"
						data-component="location-map-none"
					>
						None
					</Typography>
				)}
			</Box>
			<Box sx={{ mt: 1 }} data-component="osm-detail-info">
				<Typography
					variant="caption"
					sx={{ fontWeight: "bold" }}
					data-component="osm-detail-title"
				>
					OpenStreetMapsDetail
				</Typography>
				{ostmd ? (
					<>
						<Typography
							variant="caption"
							display="block"
							data-component="osm-detail-id"
						>
							ID: {ostmd.id}
						</Typography>
						<Typography
							variant="caption"
							display="block"
							data-component="osm-local-name"
						>
							Name: {ostmd.localname}
						</Typography>
						<Typography
							variant="caption"
							display="block"
							data-component="osm-country"
						>
							Country: {ostmd.country} ({ostmd.country_code_iso2})
						</Typography>
						<Typography
							variant="caption"
							display="block"
							data-component="osm-admin-level"
						>
							Admin Level: {ostmd.admin_level}
						</Typography>
						<Typography
							variant="caption"
							display="block"
							data-component="osm-id"
						>
							OSM ID: {ostmd.full_osm_id}
						</Typography>
					</>
				) : (
					<Typography
						variant="caption"
						display="block"
						data-component="osm-detail-none"
					>
						None
					</Typography>
				)}
			</Box>
		</Box>
	);

	return (
		<Tooltip
			data-component="location-tooltip"
			title={tooltipContent}
			onOpen={fetchLocationData}
			arrow
			placement="right"
		>
			{children ? (
				<Box sx={{ cursor: "help", display: "inline-block" }}>{children}</Box>
			) : (
				<Typography sx={{ cursor: "help" }} data-component="location-text">
					{locationText}
				</Typography>
			)}
		</Tooltip>
	);
};
